import React from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { eventPropsToString } from '@pray/shared/components/events/eventUtils';
import { colors, typography } from '@pray/shared/components/foundations';
import StripeElements from '@pray/shared/components/StripeElements';
import { Checkout } from '@pray/shared/components/ui/Checkout';
import Text from '@pray/shared/components/ui/Text';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '@pray/shared/config';
import type { IntervalPlan } from '@pray/shared/services/studioService/credits/types';
import { formatCurrency, formatNumber } from '@pray/shared/utils';

import { VIEW_EVENT_NAMES } from 'constants.js';

import CheckoutCreditPlanForm from './CheckoutCreditPlanForm/CheckoutCreditPlanForm';

type CheckoutProps = {
  title: string;
  subtitle: string;
};

type CheckoutCreditPlanProps = CheckoutProps & {
  product: IntervalPlan;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: any;
  onSuccess?: () => void;
};

export default function CheckoutCreditPlan({ title, subtitle, form, product, onSuccess }: CheckoutCreditPlanProps) {
  const { amount, period } = priceDisplay(product);
  const checkoutDescription = !product.is_free_trial_enabled
    ? '*Secure for peace of mind. Cancel easily anytime.'
    : null;

  const eventProps = eventPropsToString({
    sku: product.sku,
    price: product.price,
  });

  return (
    <div data-viewname={VIEW_EVENT_NAMES.CHECKOUT.CREDITS_PLAN_CHECKOUT} data-eventprops={eventProps}>
      <GoogleReCaptchaProvider reCaptchaKey={GOOGLE_RECAPTCHA_SITE_KEY}>
        <Checkout.Root>
          <Checkout.Header title={title} subtitle={subtitle}>
            <div className="mt-3 flex flex-col gap-1 md:mt-4 md:gap-2">
              <Text variant={typography.body_lg} color={colors.text_secondary}>
                Selected plan
              </Text>
              <Text variant={typography.heading_xl}>{product.name}</Text>
            </div>
          </Checkout.Header>
          <Checkout.Pricing>
            <Checkout.PriceDisplay amount={amount} period={period} description={checkoutDescription} />
            <Checkout.FreeTrialInfo product={product} />
          </Checkout.Pricing>
          <StripeElements>
            <Checkout.Payment>
              <CheckoutCreditPlanForm form={form} product={product} onSuccess={onSuccess} />
            </Checkout.Payment>
          </StripeElements>
        </Checkout.Root>
      </GoogleReCaptchaProvider>
    </div>
  );
}

const priceDisplay = (product: IntervalPlan) => {
  const interval = product.subscription_interval;
  const monthCount = interval === 'year' ? 12 : 1;
  const credits = product.credits_granted / monthCount;

  return {
    amount: formatCurrency(product.price),
    period: `per ${interval} | ${formatNumber(credits)} credits/mo`,
  };
};
